import { useEffect, useState } from "react";
import { Download, WifiOff, X } from "lucide-react";
import { BRAND } from "@/brand";
import { useLang } from "@/i18n";

const KEY = "vacs-install-dismissed";
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function OfflineBanner() {
  const [off, setOff] = useState(!navigator.onLine);
  const { t } = useLang();
  useEffect(() => {
    const on = () => setOff(false); const offf = () => setOff(true);
    window.addEventListener("online", on); window.addEventListener("offline", offf);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", offf); };
  }, []);
  if (!off) return null;
  return <div role="status" className="fixed inset-x-0 top-0 z-[300] flex items-center justify-center gap-2 bg-black px-3 py-2 text-xs font-semibold text-[#FFD000]" data-testid="offline-banner"><WifiOff className="h-4 w-4" /> {t("You're offline. Nothing can be saved until your connection is back.")}</div>;
}

export function InstallApp() {
  const [evt, setEvt] = useState(null);
  const [ios, setIos] = useState(false);
  const [show, setShow] = useState(false);
  const { t } = useLang();
  useEffect(() => {
    if (standalone() || localStorage.getItem(KEY)) return undefined;
    const h = (e) => { e.preventDefault(); setEvt(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", h);
    const done = () => { setShow(false); setEvt(null); };
    window.addEventListener("appinstalled", done);
    if (isIOS()) { setIos(true); setShow(true); }
    return () => { window.removeEventListener("beforeinstallprompt", h); window.removeEventListener("appinstalled", done); };
  }, []);
  if (!show) return null;
  const dismiss = () => { localStorage.setItem(KEY, "1"); setShow(false); };
  const install = async () => { evt.prompt(); const r = await evt.userChoice; if (r.outcome === "accepted") setShow(false); setEvt(null); };
  return (
    <div className="fixed bottom-20 right-3 z-[250] w-[min(340px,calc(100vw-24px))] rounded-xl border bg-card p-3 text-sm shadow-lg md:bottom-3" data-testid="install-app-card">
      <button className="absolute right-2 top-2 text-muted-foreground" onClick={dismiss} aria-label={t("Dismiss")} data-testid="install-app-dismiss"><X className="h-4 w-4" /></button>
      <div className="flex items-center gap-2 font-semibold"><img src={BRAND.icon} alt="" className="h-6 w-6 rounded" /> {t("Get {name} on this device", { name: BRAND.shortName })}</div>
      <p className="mt-1 text-xs text-muted-foreground">{t("Keep using it in your browser, or add it to your home screen for quicker, app-like access. No download from an app store is needed.")}</p>
      {ios ? <p className="mt-2 text-xs" data-testid="install-ios-steps">{t("On iPhone or iPad (Safari): tap")} <b>{t("Share")}</b>, {t("then")} <b>{t("Add to Home Screen")}</b>.</p>
        : evt && <button onClick={install} className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#00A9D7] px-3 py-1.5 text-xs font-semibold text-black" data-testid="install-app-btn"><Download className="h-3.5 w-3.5" /> {t("Install app")}</button>}
    </div>
  );
}
